import React, { useState } from "react";
import PaymentDetails from "./PaymentDetails";
import { getCurrentDate } from "../../components/common/utlity/intialValues";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Field } from "formik";

const PaymentMethodSelector = ({ options, selectedOption, onSelect }) => {
  const { t } = useTranslation();

  return (
    <div className='gap-4'>
      <div className='text-blue-600'>{t("SALE.PAYMENT_METHOD")}</div>
      <div className='mt-2 flex gap-2'>
        {options.map((option) => (
          <div
            key={option}
            className='bg-gray-300  p-2 rounded w-1/2 flex items-center dark:bg-gray-800'
          >
            <input
              id={`payment-method-${option.toLowerCase().replace(/\s/g, "-")}`}
              type='checkbox'
              checked={selectedOption === option}
              onChange={() => onSelect(option)}
              className='w-4 h-4 text-blue-600 bg-gray-700 border-gray-300 rounded '
            />
            <label
              htmlFor={`payment-method-${option
                .toLowerCase()
                .replace(/\s/g, "-")}`}
              className='ml-2 text-sm font-medium text-blue-900 dark:text-white'
            >
              {option}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

const SalePaylater = ({
  handleChangePaymentMethod,
  totalAmount,
  values,
  isPurchase,
  tickAdvance,
  advanceAmount,
  tickPartial,
  partialAmount,
}) => {
  const { t } = useTranslation();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("Cash");
  const [paidAmount, setPaidAmount] = useState(0);
  const [committedDay, setCommittedDay] = useState(getCurrentDate());
  const [saleDate, setSaleDate] = useState(getCurrentDate());

  const [isPartial, setIsPartial] = useState(tickPartial || false);
  const [isAdvance, setIsAdvance] = useState(tickAdvance || false);

  const [errors, setErrors] = useState({
    paidAmount: "",
    committedDay: "",
  });

  const isFutureDate = (date) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const selectedDate = new Date(date).setHours(0, 0, 0, 0);
    return selectedDate > today;
  };

  const handlePaidAmountChange = (amount) => {
    const newPaidAmount = parseFloat(amount) || 0;
    const dueAmount = totalAmount || 0;
    let error = "";

    if (newPaidAmount > dueAmount) {
      error = "Paid amount cannot be greater than the due amount.";
    }

    setErrors((prev) => ({ ...prev, paidAmount: error }));
    setPaidAmount(newPaidAmount);
  };

  const handleCommitmentChange = (date) => {
    let error = "";
    if (!isFutureDate(date)) {
      error = "Commitment date must be in the future.";
    }

    setErrors((prev) => ({ ...prev, committedDay: error }));
    setCommittedDay(date);
  };

  const handleSubmit = () => {
    if (errors.paidAmount || errors.committedDay) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    values["paymentMode"] = selectedPaymentMethod;
    values["paidAmount"] = paidAmount;
    values["committedDateToPay"] = committedDay;
    values["remainingTransactionAmount"] = parseFloat(
      totalAmount - paidAmount
    ).toFixed(2);

    if (isPurchase) {
      values["isPartial"] = isPartial;
      values["isAdvance"] = isAdvance;
      values["saleDate"] = saleDate;
    }

    handleChangePaymentMethod(values, true);
  };

  return (
    <div className='flex flex-col justify-end p-4'>
      {isPurchase && (
        <div className='flex gap-4 mb-4'>
          <FormControlLabel
            control={
              <Field
                as={Checkbox}
                name='isPartial'
                checked={isPartial}
                onChange={() => {
                  setIsPartial(true);
                  setIsAdvance(false);
                }}
                disabled={isAdvance || tickAdvance} // Disable if isAdvance is true OR tickAdvance is true
              />
            }
            label='Partial'
          />
          <FormControlLabel
            control={
              <Field
                as={Checkbox}
                name='isAdvance'
                checked={isAdvance}
                onChange={() => {
                  setIsAdvance(true);
                  setIsPartial(false);
                }}
                disabled={isPartial || tickPartial} // Disable if isPartial is true OR tickPartial is true
              />
            }
            label='Advance'
          />
          {advanceAmount > 0 && (
            <span className='p-2'>Advance Amount: {advanceAmount}</span>
          )}
          {tickPartial && (
            <span className='p-2'>Partial Amount: {partialAmount}</span>
          )}
        </div>
      )}

      <PaymentMethodSelector
        options={["UPI", "Cash"]}
        selectedOption={selectedPaymentMethod}
        onSelect={setSelectedPaymentMethod}
      />

      <PaymentDetails
        handlePaidAmountChange={handlePaidAmountChange}
        handleCommitmentChange={handleCommitmentChange}
        isPurchase={true}
        handleSaleDateChange={setSaleDate}
        isAdvance={isAdvance}
      />

      {/* Paid Amount Error Message */}
      {errors.paidAmount && (
        <div className='text-red-500 mt-1 text-sm'>{errors.paidAmount}</div>
      )}

      {/* Commitment Date Error Message */}
      {errors.committedDay && (
        <div className='text-red-500 mt-1 text-sm'>{errors.committedDay}</div>
      )}

      <div className='flex justify-between items-center mt-4'>
        <div className='mr-auto'>
          <div className='border-b border-blue-600 text-red-500 inline-block'>
            {t("SALE.DUE_AMOUNT")}
          </div>
          <div className='border-blue-600 text-red-500'>
            Rs {(totalAmount - paidAmount).toFixed(2) || 0}
          </div>
        </div>

        <button
          className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded 
            ${
              errors.paidAmount || errors.committedDay
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          onClick={handleSubmit}
          disabled={errors.paidAmount || errors.committedDay}
        >
          {isPurchase ? "Ok" : t("SALE.PAY_NOW")}
        </button>
      </div>
    </div>
  );
};

export default SalePaylater;
